/**
 * Language utilities for i18n support.
 *
 * Supported languages:
 *   - "HEB" (Hebrew, RTL) – the default
 *   - "ENG" (English, LTR)
 *   - "RUS" (Russian, LTR)
 *
 * Adding a new language:
 *   1. Add it to the `Language` union and the `LANGUAGES` array.
 *   2. Add entries to every Record below (HTML_LANG, HTML_DIR, etc.).
 *   3. Add a branch in every translation file under translations/.
 *
 * Language preference is persisted in a cookie so that both SSR
 * (`entry.server.tsx`) and client code can read it.
 */

export type Language = "HEB" | "ENG" | "RUS";

export const LANGUAGES: Language[] = ["HEB", "ENG", "RUS"];

export const LANG_COOKIE_NAME = "moneytor-lang";

export const DEFAULT_LANGUAGE: Language = "HEB";

/** Max-age for the language cookie – 1 year in seconds. */
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

/** HTML `lang` attribute value for each supported language. */
export const HTML_LANG: Record<Language, string> = {
  HEB: "he",
  ENG: "en",
  RUS: "ru",
};

/** HTML `dir` attribute value for each supported language. */
export const HTML_DIR: Record<Language, "rtl" | "ltr"> = {
  HEB: "rtl",
  ENG: "ltr",
  RUS: "ltr",
};

/** Language code for Open Finance API (supports he/en only). RUS etc. fall back to en. */
export const OPENFINANCE_LANG: Record<Language, string> = {
  HEB: "he",
  ENG: "en",
  RUS: "en",
};

/** Human-readable label shown in the language switcher. */
export const LANG_LABEL: Record<Language, string> = {
  HEB: "עברית",
  ENG: "English",
  RUS: "Русский",
};

/** Short label used for a compact toggle button. */
export const LANG_SHORT_LABEL: Record<Language, string> = {
  HEB: "HE",
  ENG: "EN",
  RUS: "RU",
};

/** Locale string for Intl / toLocaleString formatting. */
export const LOCALE: Record<Language, string> = {
  HEB: "he-IL",
  ENG: "en-US",
  RUS: "ru-RU",
};

// ---------------------------------------------------------------------------
// Cookie helpers – work on both server (raw header) and client
// ---------------------------------------------------------------------------

/**
 * Read the language preference from a raw `Cookie` header string (server-side)
 * or from `document.cookie` (client-side).
 */
export function readLanguageCookie(cookieString: string | null | undefined): Language {
  if (!cookieString) return DEFAULT_LANGUAGE;
  const match = cookieString.match(
    new RegExp(`(?:^|;\\s*)${LANG_COOKIE_NAME}=([^;]+)`)
  );
  const value = match?.[1]?.trim();
  if (value && (LANGUAGES as readonly string[]).includes(value)) return value as Language;
  return DEFAULT_LANGUAGE;
}

/**
 * Write the language cookie on the client (`document.cookie`).
 * Uses `path=/` so it's available on every route, and `SameSite=Lax`.
 */
export function writeLanguageCookie(lang: Language): void {
  document.cookie = `${LANG_COOKIE_NAME}=${lang}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

// ---------------------------------------------------------------------------
// Field-level localisation helper
// ---------------------------------------------------------------------------

/**
 * Pick the best available translation for a DB field.
 *
 * Fallback chain: translated value → English → Hebrew.
 *
 * @param he       Hebrew value (base column, always present)
 * @param en       English value (`_en` column)
 * @param lang     Current UI language
 * @param translated  Value from the `*_translations` table for the current
 *                    language, if one exists. Only needed for languages other
 *                    than HEB/ENG (e.g. RUS).
 */
export function localizedField(
  he: string | null | undefined,
  en: string | null | undefined,
  lang: Language,
  translated?: string | null | undefined,
): string | null | undefined {
  if (lang === "HEB") return he;
  if (lang === "ENG") return en || he;
  return translated || en || he;
}

// ---------------------------------------------------------------------------
// Hebrew month names (for filenames, e.g. דוח-מאניטור-ינואר.pdf)
// ---------------------------------------------------------------------------

/** Hebrew month names, index 0 = January … 11 = December */
export const HEBREW_MONTH_NAMES: string[] = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];

/** Previous month’s index (0–11). */
export function getPreviousMonthIndex(): number {
  const d = new Date();
  return (d.getMonth() - 1 + 12) % 12;
}

/** Year of the previous month (e.g. when in Jan 2026, returns 2025). */
export function getPreviousMonthYear(): number {
  const d = new Date();
  const year = d.getFullYear();
  return d.getMonth() === 0 ? year - 1 : year;
}

/** Filename for monthly report PDF: דוח-מאניטור-2025-ינואר.pdf (year + previous month). */
export function getMonthlyReportPdfFilenameHebrew(): string {
  const monthIndex = getPreviousMonthIndex();
  const year = getPreviousMonthYear();
  const monthName = HEBREW_MONTH_NAMES[monthIndex];
  return `דוח-מאניטור-${year}-${monthName}.pdf`;
}

/** ASCII filename for WhatsApp document headers (Meta rejects non-ASCII filenames). */
export function getMonthlyReportPdfFilenameAscii(): string {
  const monthIndex = getPreviousMonthIndex();
  const year = getPreviousMonthYear();
  return `moneytor-${year}-${String(monthIndex + 1).padStart(2, "0")}.pdf`;
}

/** Header text for monthly report PDF: דוח מאניטור חודשי - ינואר 2025 (previous month + year). */
export function getMonthlyReportHeaderHebrew(): string {
  const monthIndex = getPreviousMonthIndex();
  const year = getPreviousMonthYear();
  const monthName = HEBREW_MONTH_NAMES[monthIndex];
  return `דוח מאניטור חודשי - ${monthName} ${year}`;
}
